/**
 * @file     sessionHistory.ts
 * @purpose  Previous conversations for a returning visitor. Each finished or
 *           in-progress search is kept on the device so someone can pick a past
 *           one back up, the way an editor keeps its chat history. Device-local
 *           by design; a cross-device version would move this behind an account.
 */
'use client';

import type { TraditionalSearchValue } from '@/components/search/searchValue';
import type { AnswerListing, StraightAnswer } from './straightAnswer';
import type { DoorId, QuestionId } from './doors';
import type { MapLayerKey } from './lifestyle';
import type { CountedFit } from './fits';
import type { FinderPhase, FinderTurn } from '@/views/property-finder/usePropertyFinder';

/** One saved conversation. Enough to restore the transcript and the verdict;
 *  the live map and counts re-fetch from the filters on restore. */
export interface FinderSession {
  id: string;
  createdAt: number;
  updatedAt: number;
  doorId: DoorId;
  title: string;
  openingSentence: string;
  turns: FinderTurn[];
  filters: Partial<TraditionalSearchValue>;
  answers: Partial<Record<QuestionId, string>>;
  answer: StraightAnswer | null;
  ceiling: number | null;
  count: number | null;
  inReachCount: number | null;
  fits?: CountedFit[];
  fitHomes?: AnswerListing[];
  phase: FinderPhase;
  /** The question path this conversation took. Absent on sessions saved before
   *  the price-or-lifestyle branch existed. */
  queue?: QuestionId[];
  layers?: MapLayerKey[];
}

const KEY = 'relta:propertyFinder:history:v1';
const CAP = 20;

/** A short, human title: the opening sentence, else the verdict, else the door. */
export function deriveTitle(input: {
  openingSentence: string;
  answer: StraightAnswer | null;
  doorId: DoorId;
}): string {
  const sentence = input.openingSentence.trim();
  if (sentence) return sentence.length > 48 ? `${sentence.slice(0, 47)}…` : sentence;
  if (input.answer?.headline) {
    const h = input.answer.headline.trim();
    return h.length > 48 ? `${h.slice(0, 47)}…` : h;
  }
  const labels: Record<DoorId, string> = {
    'first-time': 'First home',
    investor: 'Rental',
    'short-term-rental': 'Nightly rental',
    'not-sure': 'New search',
  };
  return labels[input.doorId];
}

export function listSessions(): FinderSession[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return (parsed as FinderSession[])
      .filter((s) => s && typeof s.id === 'string' && Array.isArray(s.turns))
      .sort((a, b) => b.updatedAt - a.updatedAt);
  } catch {
    /* intentional: storage blocked (private mode) or an old shape; start fresh */
    return [];
  }
}

export function getSession(id: string): FinderSession | null {
  return listSessions().find((s) => s.id === id) ?? null;
}

/** Insert or replace a session, keeping the most recent CAP by update time. */
export function upsertSession(session: FinderSession): void {
  if (typeof window === 'undefined') return;
  try {
    const rest = listSessions().filter((s) => s.id !== session.id);
    const next = [session, ...rest].sort((a, b) => b.updatedAt - a.updatedAt).slice(0, CAP);
    window.localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* quota or serialization failure: history is a convenience, not load-bearing */
  }
}

export function deleteSession(id: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(KEY, JSON.stringify(listSessions().filter((s) => s.id !== id)));
  } catch {
    /* ignore */
  }
}

export function newSessionId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}
